import { CommonButton, CountrySelect, getCountryName, Modal, Select, Switch } from '@chocolate-soup-inc/cs-frontend-components';
import { startCase } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useQueryListAllCompaniesOptions } from '../../entities/companies/queries';
import { TCarriers, TCustomReportEntity, TEventReportType, TEventTypes, TGiftStatuses, TGiftTypes, TShipmentStatuses, TShipStationAddressVerified, TWorkAnniversaryGiftVariations } from '../../generated/graphql';
import { queryCustomReport, TCustomReportRequest } from './queryCustomReport';
import { downloadReport, TReportFormat, TReportMerge, TReportSheet } from './reportExport';
import { eventReportEndDate, REPORT_START_MONTH } from './reportFilters';

import './ReportPage.scss';

const SHIPMENTS = TCustomReportEntity.Shipments;
const EVENTS = TCustomReportEntity.Events;
const GIFTS = TCustomReportEntity.Gifts;
const CUSTOM_RANGE = 25;
const DATE_RANGES = [
  { label: 'Current month', value: 0 },
  { label: 'Last month', value: 1 },
  { label: 'Last 2 months', value: 2 },
  { label: 'Last 3 months', value: 3 },
  { label: 'Last 6 months', value: 6 },
  { label: 'Last 9 months', value: 9 },
  { label: 'Last year (12 months)', value: 12 },
  { label: 'Last 18 months', value: 18 },
  { label: 'Last 2 years (24 months)', value: 24 },
  { label: 'Custom…', value: CUSTOM_RANGE }
];
const FUTURE_DATE_RANGES = [1, 2, 3, 6, 9, 12, 18, 24].map((months) => ({ label: `Next ${months} ${months === 1 ? 'month' : 'months'}`, value: -months }));
const RECURRING_EVENT_TYPES = [TEventTypes.Birthday, TEventTypes.WorkAnniversary, TEventTypes.NewHire];
const options = (values: string[]) => values.map((value) => ({ label: startCase(value), value }));
type TBooleanFilter = 'companyActive' | 'eventSkipped' | 'eventPaused' | 'eventHasGift' | 'eventDelivered' | 'giftHasShipment';
const currentMonth = () => new Date().toISOString().slice(0, 7);

const getPresetRange = (range: number) => {
  if (range < 0) {
    const start = new Date(`${currentMonth()}-01T00:00:00.000Z`);
    start.setUTCMonth(start.getUTCMonth() + 1);
    const end = new Date(start);
    end.setUTCMonth(end.getUTCMonth() - range - 1);
    return { startMonth: start.toISOString().slice(0, 7), endMonth: end.toISOString().slice(0, 7) };
  }
  const end = new Date(`${currentMonth()}-01T00:00:00.000Z`);
  if (range > 0) end.setUTCMonth(end.getUTCMonth() - 1);
  const start = new Date(end);
  if (range > 0) start.setUTCMonth(start.getUTCMonth() - range + 1);
  return { startMonth: start.toISOString().slice(0, 7), endMonth: end.toISOString().slice(0, 7) };
};

const getMonths = (startMonth: string, endMonth: string) => {
  const months: string[] = [];
  const current = new Date(`${startMonth}-01T00:00:00.000Z`);
  const end = new Date(`${endMonth}-01T00:00:00.000Z`);
  while (current <= end) {
    months.push(current.toISOString().slice(0, 7));
    current.setUTCMonth(current.getUTCMonth() + 1);
  }
  return months;
};

export const CustomReportModal = ({ entity, onClose }: { entity: TCustomReportEntity; onClose: () => void }) => {
  const { data: companies, loading: companiesLoading, error: companiesError } = useQueryListAllCompaniesOptions();
  const [parameters, setParameters] = useState<Omit<TCustomReportRequest, 'month' | 'entity' | 'eventReportType'>>({});
  const [eventReportType, setEventReportType] = useState(TEventReportType.Existing);
  const expectedEvents = entity === EVENTS && eventReportType === TEventReportType.Expected;
  const eventReportLabel = expectedEvents ? 'Expected events' : 'Already existing events';
  const reportLabel = entity === EVENTS ? eventReportLabel : startCase(entity);
  const reportName = entity === EVENTS ? `events-${eventReportType}` : entity;
  const [dateRange, setDateRange] = useState(0);
  const [customRange, setCustomRange] = useState(() => getPresetRange(0));
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);

  const update = <K extends keyof typeof parameters>(key: K, value: (typeof parameters)[K]) => setParameters((previous) => ({ ...previous, [key]: value }));
  const booleanFilterText = (field: TBooleanFilter, yes: string, no: string) => (parameters[field] == null ? 'No restriction' : parameters[field] ? yes : no);
  const booleanFilter = (field: TBooleanFilter, label: string, yes: string, no: string) => (
    <div className='custom-report-toggle' key={field}>
      <div className='custom-report-toggle-control'>
        <label htmlFor={field}>{label}</label>
        <Switch name={field} disabled={busy} value={parameters[field] === true} onChange={(value) => update(field, value === true)} />
      </div>
      <div className='custom-report-toggle-details'>
        <span>{booleanFilterText(field, yes, no)}</span>
        <button type='button' disabled={busy || parameters[field] == null} onClick={() => update(field, null)}>
          Include all
        </button>
      </div>
    </div>
  );
  const { startMonth, endMonth } = dateRange === CUSTOM_RANGE ? customRange : getPresetRange(dateRange);
  const lastMonth = entity === EVENTS ? eventReportEndDate().slice(0, 7) : currentMonth();
  const dateRanges = entity === EVENTS ? [...DATE_RANGES.filter((range) => range.value !== CUSTOM_RANGE), ...FUTURE_DATE_RANGES, { label: 'Custom…', value: CUSTOM_RANGE }] : DATE_RANGES;
  const monthOptions = getMonths(REPORT_START_MONTH, lastMonth).map((month) => ({ label: month, value: month }));
  const selectProps = { variant: 'outlined' as const, disabled: busy };
  let typeLabel = 'Included gift types';
  let typeOptions = Object.values(TGiftTypes) as string[];
  if (entity === EVENTS) {
    typeLabel = 'Event types';
    typeOptions = expectedEvents ? RECURRING_EVENT_TYPES : Object.values(TEventTypes);
  } else if (entity === GIFTS) typeLabel = 'Gift types';

  const stateFilters: { field: Exclude<TBooleanFilter, 'companyActive'>; label: string; yes: string; no: string }[] = [];
  if (entity === EVENTS && !expectedEvents) {
    stateFilters.push(
      { field: 'eventSkipped', label: 'Skipped events', yes: 'Skipped only', no: 'Not skipped' },
      { field: 'eventPaused', label: 'Paused events', yes: 'Paused only', no: 'Not paused' },
      { field: 'eventHasGift', label: 'Gift generation', yes: 'With a gift', no: 'Without a gift' },
      { field: 'eventDelivered', label: 'Event delivery', yes: 'Delivered only', no: 'Not delivered' }
    );
  } else if (entity === GIFTS) stateFilters.push({ field: 'giftHasShipment', label: 'Shipment assignment', yes: 'Assigned to a shipment', no: 'Not assigned to a shipment' });

  let description = `Count matching records by creation month, starting from ${REPORT_START_MONTH}.`;
  if (entity === EVENTS) description = 'Count already existing events by event month.';
  if (expectedEvents) description = 'Estimate recurring events by event month using the current roster and generation rules, up to two years ahead. No events are created.';

  const generate = async (format: TReportFormat) => {
    if (controller.current) return;
    setError('');
    if (![startMonth, endMonth].every((month) => /^\d{4}-(0[1-9]|1[0-2])$/.test(month))) return setError('Select a valid start and end month.');
    if (startMonth < REPORT_START_MONTH || endMonth > lastMonth || startMonth > endMonth) return setError(`Select months from ${REPORT_START_MONTH} through ${lastMonth}, with the start before or equal to the end.`);
    const months = getMonths(startMonth, endMonth);

    const request = new AbortController();
    controller.current = request;
    setBusy(true);
    setStatus('Loading report…');
    try {
      const companiesById = new Map<string, { companyName: string; counts: number[]; categories: Record<string, number>[] }>();
      const monthTotals = Array<number>(months.length).fill(0);
      const categoryTotals = months.map(() => ({}) as Record<string, number>);
      const categories = new Set<string>();
      if (parameters.breakdownByCategory) {
        const selectedTypes = (entity === EVENTS ? parameters.eventTypes : parameters.giftTypes) || [];
        const editions = parameters.workAnniversaryVariations?.length ? parameters.workAnniversaryVariations : parameters.companyGiftVariations?.length ? parameters.companyGiftVariations : Object.values(TWorkAnniversaryGiftVariations);
        for (const type of selectedTypes.length ? selectedTypes : typeOptions) {
          if (type === TEventTypes.WorkAnniversary) for (const edition of editions) categories.add(`workAnniversary|${edition}`);
          else categories.add(type);
        }
      }
      let recordCount = 0;
      for (const [monthIndex, month] of months.entries()) {
        if (request.signal.aborted) return;
        const rows = await queryCustomReport(
          { ...parameters, entity, month, ...(entity === EVENTS ? { eventReportType } : {}), ...(expectedEvents ? { excludePausedEmployees: parameters.excludePausedEmployees !== false } : {}) },
          request.signal
        );
        if (request.signal.aborted) return;
        for (const item of rows) {
          const company = companiesById.get(item.companyId) || { companyName: item.companyName, counts: Array<number>(months.length).fill(0), categories: months.map(() => ({}) as Record<string, number>) };
          company.counts[monthIndex] += item.count;
          for (const [category, count] of Object.entries(item.categories || {})) {
            categories.add(category);
            company.categories[monthIndex][category] = (company.categories[monthIndex][category] || 0) + count;
            categoryTotals[monthIndex][category] = (categoryTotals[monthIndex][category] || 0) + count;
          }
          companiesById.set(item.companyId, company);
          monthTotals[monthIndex] += item.count;
          recordCount += item.count;
        }
        setStatus(`${month}: ${recordCount} records loaded…`);
      }
      const selectedCompanies = (parameters.companyIds || []).map((id) => `${companies.find((company) => company.id === id)?.name || id} (${id})`).join(', ');
      const sheet: TReportSheet = [
        ['Report', reportLabel],
        ['Date range', dateRanges.find((range) => range.value === dateRange)?.label],
        ['From month', startMonth],
        ['Through month', endMonth],
        ['Date field', entity === EVENTS ? 'Event date' : 'Created at'],
        ['Calendar', 'UTC'],
        ['Measure', `${reportLabel} count`],
        ['Break down by category', parameters.breakdownByCategory ? 'Yes' : 'No'],
        ['Companies', selectedCompanies || 'All'],
        ['Company activity', booleanFilterText('companyActive', 'Active companies only', 'Inactive companies only')],
        ['Company anniversary editions', parameters.companyGiftVariations?.map(startCase).join(', ') || 'All'],
        [typeLabel, (entity === EVENTS ? parameters.eventTypes : parameters.giftTypes)?.map(startCase).join(', ') || 'All'],
        ['Work anniversary editions', parameters.workAnniversaryVariations?.map(startCase).join(', ') || 'All'],
        ['Address countries', parameters.countries?.map((country) => getCountryName(country) || country).join(', ') || 'All'],
        ['Company and address data', 'Current values'],
        ['Records', recordCount]
      ];
      if (entity === EVENTS) sheet.push(['Future dates through', eventReportEndDate()]);
      if (expectedEvents) sheet.push(['Exclude Paused Employees', parameters.excludePausedEmployees === false ? 'No' : 'Yes']);
      if (entity === SHIPMENTS)
        sheet.push(
          ['Shipment statuses', parameters.shipmentStatuses?.map(startCase).join(', ') || 'All'],
          ['Carriers', parameters.shipmentCarriers?.map(startCase).join(', ') || 'All'],
          ['Address verification', parameters.shipmentAddressVerification?.map(startCase).join(', ') || 'All']
        );
      if (entity === GIFTS) sheet.push(['Gift statuses', parameters.giftStatuses?.map(startCase).join(', ') || 'All']);
      for (const { field, label, yes, no } of stateFilters) sheet.push([label, booleanFilterText(field, yes, no)]);
      const merges: TReportMerge[] = [];
      const categoryKeys = [...categories];
      sheet.push([]);
      if (parameters.breakdownByCategory) {
        if (entity === SHIPMENTS) sheet.push(['Category counts', 'Each shipment counts once per matching category. TOTAL counts each shipment once.'], []);
        sheet.push(['Company ID', 'Company', ...months.flatMap((month) => categoryKeys.map(() => month)), 'TOTAL']);
        const headerRow = sheet.length;
        const categoryLabels = categoryKeys.map((key) => {
          const [type, edition] = key.split('|');
          return edition ? `${startCase(edition)} ${startCase(type)}` : startCase(type);
        });
        sheet.push(['', '', ...months.flatMap(() => categoryLabels), '']);
        if (categoryKeys.length > 1)
          for (const [index] of months.entries()) {
            // ExcelJS coordinates start at one; the first two columns identify the company.
            const firstColumn = 3 + index * categoryKeys.length;
            merges.push([headerRow, firstColumn, headerRow, firstColumn + categoryKeys.length - 1]);
          }
      } else sheet.push(['Company ID', 'Company', ...months, 'TOTAL']);
      for (const [companyId, company] of [...companiesById.entries()].sort(([leftId, left], [rightId, right]) => left.companyName.localeCompare(right.companyName) || leftId.localeCompare(rightId)))
        sheet.push([
          companyId,
          company.companyName,
          ...(parameters.breakdownByCategory ? company.categories.flatMap((counts) => categoryKeys.map((category) => counts[category] || 0)) : company.counts),
          company.counts.reduce((total, count) => total + count, 0)
        ]);
      sheet.push(['', 'TOTAL', ...(parameters.breakdownByCategory ? categoryTotals.flatMap((counts) => categoryKeys.map((category) => counts[category] || 0)) : monthTotals), recordCount]);
      await downloadReport(sheet, `cs-custom-${reportName}_${startMonth}_${endMonth}`, format, request.signal, merges);
      if (!request.signal.aborted) setStatus(`${format.toUpperCase()} generated: ${recordCount} matching records across ${companiesById.size} companies.`);
    } catch (cause) {
      if (!request.signal.aborted) {
        setError(cause instanceof Error ? cause.message : 'Report generation failed. Please try again.');
        setStatus('');
      }
    } finally {
      controller.current = null;
      if (!request.signal.aborted) setBusy(false);
    }
  };

  return (
    <Modal
      headline={`Custom ${startCase(entity)} Report`}
      size='large'
      closeModal={onClose}
      supportingText={`${description} Empty selections include all values. Company and address filters use current data.`}
      showCustomButton={true}
      customButton={() => (
        <div className='custom-report-actions'>
          <CommonButton label='Close' onClick={onClose} variant='text' />
          <CommonButton label='Export CSV' leadingIcon='download' disabled={busy || companiesLoading || !!companiesError} onClick={() => generate('csv')} variant='outlined' />
          <CommonButton label='Export XLSX' leadingIcon='download' disabled={busy || companiesLoading || !!companiesError} onClick={() => generate('xlsx')} variant='filled' />
        </div>
      )}>
      <fieldset className='custom-report-form' disabled={busy}>
        {entity === EVENTS && (
          <Select
            {...selectProps}
            label='Report type'
            name='eventReportType'
            multiple={false}
            includeEmptyOption={false}
            options={[
              { label: 'Already existing events', value: TEventReportType.Existing },
              { label: 'Expected events', value: TEventReportType.Expected }
            ]}
            value={eventReportType}
            onChange={(value) => {
              if (value !== TEventReportType.Existing && value !== TEventReportType.Expected) return;
              setEventReportType(value);
              setParameters((previous) => ({
                ...previous,
                eventTypes: previous.eventTypes?.filter((type) => value !== TEventReportType.Expected || RECURRING_EVENT_TYPES.includes(type)),
                excludePausedEmployees: value === TEventReportType.Expected ? true : undefined,
                eventSkipped: undefined,
                eventPaused: undefined,
                eventHasGift: undefined,
                eventDelivered: undefined
              }));
            }}
          />
        )}
        {expectedEvents && (
          <div className='custom-report-toggle'>
            <div className='custom-report-toggle-control'>
              <label htmlFor='excludePausedEmployees'>Exclude Paused Employees</label>
              <Switch disabled={busy} name='excludePausedEmployees' value={parameters.excludePausedEmployees !== false} onChange={(value) => update('excludePausedEmployees', value === true)} />
            </div>
          </div>
        )}
        <div className='custom-report-toggle'>
          <div className='custom-report-toggle-control'>
            <label htmlFor='breakdownByCategory'>Break down by category</label>
            <Switch disabled={busy} name='breakdownByCategory' value={parameters.breakdownByCategory || false} onChange={(value) => update('breakdownByCategory', value === true)} />
          </div>
          <div className='custom-report-toggle-details'>Add type and anniversary-edition columns under each month.</div>
        </div>
        <Select
          {...selectProps}
          label='Date range'
          name='dateRange'
          multiple={false}
          includeEmptyOption={false}
          orderByValue={true}
          orderDirection='asc'
          options={dateRanges}
          value={dateRange}
          onChange={(value) => {
            const range = Number(value);
            if (!dateRanges.some((option) => option.value === range)) return;
            if (range === CUSTOM_RANGE) setCustomRange({ startMonth, endMonth });
            setDateRange(range);
          }}
        />
        {dateRange === CUSTOM_RANGE && (
          <>
            <Select
              {...selectProps}
              label='From month'
              name='startMonth'
              multiple={false}
              includeEmptyOption={false}
              orderDirection='desc'
              options={monthOptions}
              value={startMonth}
              onChange={(value) => setCustomRange((previous) => ({ ...previous, startMonth: String(value) }))}
            />
            <Select
              {...selectProps}
              label='Through month'
              name='endMonth'
              multiple={false}
              includeEmptyOption={false}
              orderDirection='desc'
              options={monthOptions}
              value={endMonth}
              onChange={(value) => setCustomRange((previous) => ({ ...previous, endMonth: String(value) }))}
            />
          </>
        )}
        <Select
          {...selectProps}
          label='Companies'
          name='companyIds'
          multiple={true}
          options={companies.map((company) => ({ label: company.name, value: company.id }))}
          value={parameters.companyIds || []}
          onChange={(value) => update('companyIds', value as string[])}
          supportingText='Empty includes every company.'
        />
        {booleanFilter('companyActive', 'Company activity', 'Active companies only', 'Inactive companies only')}
        <Select
          {...selectProps}
          label='Company anniversary editions'
          name='companyGiftVariations'
          multiple={true}
          options={options(Object.values(TWorkAnniversaryGiftVariations))}
          value={parameters.companyGiftVariations || []}
          onChange={(value) => update('companyGiftVariations', value as TWorkAnniversaryGiftVariations[])}
          supportingText='Limits every record, including birthdays, to companies with these editions.'
        />
        <Select
          {...selectProps}
          label={typeLabel}
          name={entity === EVENTS ? 'eventTypes' : 'giftTypes'}
          multiple={true}
          options={options(typeOptions)}
          value={(entity === EVENTS ? parameters.eventTypes : parameters.giftTypes) || []}
          onChange={(value) => (entity === EVENTS ? update('eventTypes', value as TEventTypes[]) : update('giftTypes', value as TGiftTypes[]))}
          supportingText={entity === SHIPMENTS ? 'Matches at least one attached gift. Each shipment counts once.' : undefined}
        />
        <Select
          {...selectProps}
          label='Work anniversary editions'
          name='workAnniversaryVariations'
          multiple={true}
          options={options(Object.values(TWorkAnniversaryGiftVariations))}
          value={parameters.workAnniversaryVariations || []}
          onChange={(value) => update('workAnniversaryVariations', value as TWorkAnniversaryGiftVariations[])}
          supportingText='Restricts work anniversaries only; birthdays remain eligible.'
        />
        <CountrySelect
          {...selectProps}
          label='Address country'
          name='countries'
          includeEmptyOption={true}
          emptyOptionLabel='All countries'
          value={parameters.countries?.[0] || null}
          onChange={(value) => update('countries', value ? [String(value)] : [])}
        />
        {entity === SHIPMENTS && (
          <>
            <Select
              {...selectProps}
              label='Shipment statuses'
              name='shipmentStatuses'
              multiple={true}
              options={options(Object.values(TShipmentStatuses))}
              value={parameters.shipmentStatuses || []}
              onChange={(value) => update('shipmentStatuses', value as TShipmentStatuses[])}
            />
            <Select
              {...selectProps}
              label='Carriers'
              name='shipmentCarriers'
              multiple={true}
              options={options(Object.values(TCarriers))}
              value={parameters.shipmentCarriers || []}
              onChange={(value) => update('shipmentCarriers', value as TCarriers[])}
              supportingText='Uses the carrier on the shipment label.'
            />
            <Select
              {...selectProps}
              label='Address verification'
              name='shipmentAddressVerification'
              multiple={true}
              options={options(Object.values(TShipStationAddressVerified))}
              value={parameters.shipmentAddressVerification || []}
              onChange={(value) => update('shipmentAddressVerification', value as TShipStationAddressVerified[])}
            />
          </>
        )}
        {entity === GIFTS && (
          <Select
            {...selectProps}
            label='Gift statuses'
            name='giftStatuses'
            multiple={true}
            options={options(Object.values(TGiftStatuses))}
            value={parameters.giftStatuses || []}
            onChange={(value) => update('giftStatuses', value as TGiftStatuses[])}
          />
        )}
        {stateFilters.map(({ field, label, yes, no }) => booleanFilter(field, label, yes, no))}
      </fieldset>
      {(error || companiesError) && <p role='alert'>{error || companiesError?.message}</p>}
      <p aria-live='polite'>{status}</p>
    </Modal>
  );
};
